import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Node, useEditor } from '@splashthat/splash-cms-engine';
import { SuiNotificationGroup, SuiSpinner } from '@splashthat/sui';
import { useSessionRegistration } from 'common/components/TouchpointEditor/hooks/useSessionRegistration';
import { getRegisteredSessionsStatus } from 'common/components/TouchpointEditor/utils/index';
import SessionsBlock from './nodes/Block/SubTypes/SessionsBlock';
import {
    NODE_DISPLAY_NAME_PAST_SESSION_CARDS,
    NODE_DISPLAY_NAME_PAST_SESSIONS_BLOCK,
    NODE_DISPLAY_NAME_PAST_SESSIONS_HEADLINE,
    NODE_DISPLAY_NAME_PAST_SESSIONS_PARAGRAPH,
    NODE_DISPLAY_NAME_UPCOMING_SESSION_CARDS,
    NODE_DISPLAY_NAME_UPCOMING_SESSIONS_BLOCK,
    NODE_DISPLAY_NAME_UPCOMING_SESSIONS_HEADLINE,
    NODE_DISPLAY_NAME_UPCOMING_SESSIONS_PARAGRAPH,
    NODE_ID_PAST_SESSION_CARDS,
    NODE_ID_PAST_SESSIONS_BLOCK,
    NODE_ID_PAST_SESSIONS_HEADLINE,
    NODE_ID_PAST_SESSIONS_PARAGRAPH,
    NODE_ID_UPCOMING_SESSION_CARDS,
    NODE_ID_UPCOMING_SESSIONS_BLOCK,
    NODE_ID_UPCOMING_SESSIONS_HEADLINE,
    NODE_ID_UPCOMING_SESSIONS_PARAGRAPH,
} from './constants';
import { SessionsBlockContentContainer } from './Livestream.styles';
import UpcomingSessionsHeadline from './nodes/Headline/SubTypes/UpcomingSessionsHeadline/UpcomingSessionsHeadline';
import SessionListParagraph from './nodes/Paragraph/SubTypes/SessionListParagraph/SessionListParagraph';
import PastSessionListParagraph from './nodes/Paragraph/SubTypes/PastSessionListParagraph/PastSessionListParagraph';
import AttendeeSessionsList from './nodes/AttendeesSessionsList';
import PastAttendeeSessionsList from './nodes/AttendeesSessionsList/PastAttendeesSessionsList';
import { useAttendeeSessions } from '../../../../../components/TouchpointEditor/contexts/AttendeeSessionContext';
import PastSessionsBlock from './nodes/Block/SubTypes/PastSessionsBlock';
import PastSessionsHeadline from './nodes/Headline/SubTypes/PastSessionsHeadline/PastSessionsHeadline';
import { LoadingContainer } from './nodes/AttendeesSessionsList/AttendeeSessionsList.styles';

export function Sessions({ structure }) {
    const editor = useEditor((state) => ({ editing: state.editing }));
    const attendeeSessions = useAttendeeSessions();
    const sessionRegistration = useSessionRegistration();
    const [notificationDismissed, setNotificationDismissed] = useState(false);

    if (attendeeSessions.isLoading) {
        return (
            <LoadingContainer data-qa="sessions-loading-spinner">
                <SuiSpinner loading size="large" aria-label="loading" />
            </LoadingContainer>
        );
    }

    const activeNotifications = attendeeSessions.isError
        ? [
              {
                  message: 'There has been an error retrieving the list of sessions.',
                  variation: 'ERROR',
                  onClose: () => setNotificationDismissed(true),
              },
          ]
        : [];
    const hasSessions = (sessions) =>
        editor.editing ? sessions.length > 0 : getRegisteredSessionsStatus(sessionRegistration, sessions).length > 0;
    const shouldShowUpcomingSessions = hasSessions(attendeeSessions.sessions);
    const shouldShowPastSessions = hasSessions(attendeeSessions.pastSessions);

    return (
        <>
            <SuiNotificationGroup notifications={notificationDismissed ? [] : activeNotifications} />
            {shouldShowUpcomingSessions ? (
                <Node
                    is={SessionsBlock}
                    id={NODE_ID_UPCOMING_SESSIONS_BLOCK}
                    displayName={NODE_DISPLAY_NAME_UPCOMING_SESSIONS_BLOCK}
                    {...structure[NODE_ID_UPCOMING_SESSIONS_BLOCK]}
                >
                    <SessionsBlockContentContainer>
                        <Node
                            is={UpcomingSessionsHeadline}
                            id={NODE_ID_UPCOMING_SESSIONS_HEADLINE}
                            displayName={NODE_DISPLAY_NAME_UPCOMING_SESSIONS_HEADLINE}
                            {...structure[NODE_ID_UPCOMING_SESSIONS_HEADLINE]}
                        />
                        <Node
                            is={SessionListParagraph}
                            id={NODE_ID_UPCOMING_SESSIONS_PARAGRAPH}
                            displayName={NODE_DISPLAY_NAME_UPCOMING_SESSIONS_PARAGRAPH}
                            {...structure[NODE_ID_UPCOMING_SESSIONS_PARAGRAPH]}
                        />
                        <Node
                            is={AttendeeSessionsList}
                            id={NODE_ID_UPCOMING_SESSION_CARDS}
                            displayName={NODE_DISPLAY_NAME_UPCOMING_SESSION_CARDS}
                            {...structure[NODE_ID_UPCOMING_SESSION_CARDS]}
                            status={'upcoming'}
                        />
                    </SessionsBlockContentContainer>
                </Node>
            ) : null}
            {shouldShowPastSessions ? (
                <Node
                    is={PastSessionsBlock}
                    id={NODE_ID_PAST_SESSIONS_BLOCK}
                    displayName={NODE_DISPLAY_NAME_PAST_SESSIONS_BLOCK}
                    {...structure[NODE_ID_PAST_SESSIONS_BLOCK]}
                >
                    <SessionsBlockContentContainer>
                        <Node
                            is={PastSessionsHeadline}
                            id={NODE_ID_PAST_SESSIONS_HEADLINE}
                            displayName={NODE_DISPLAY_NAME_PAST_SESSIONS_HEADLINE}
                            {...structure[NODE_ID_PAST_SESSIONS_HEADLINE]}
                        />
                        <Node
                            is={PastSessionListParagraph}
                            id={NODE_ID_PAST_SESSIONS_PARAGRAPH}
                            displayName={NODE_DISPLAY_NAME_PAST_SESSIONS_PARAGRAPH}
                            {...structure[NODE_ID_PAST_SESSIONS_PARAGRAPH]}
                        />
                        <Node
                            is={PastAttendeeSessionsList}
                            id={NODE_ID_PAST_SESSION_CARDS}
                            displayName={NODE_DISPLAY_NAME_PAST_SESSION_CARDS}
                            {...structure[NODE_ID_PAST_SESSION_CARDS]}
                            status={'past'}
                        />
                    </SessionsBlockContentContainer>
                </Node>
            ) : null}
        </>
    );
}

Sessions.propTypes = {
    structure: PropTypes.object,
};

Sessions.defaultProps = {
    structure: null,
    guest: {},
};
